import { Injectable, Injector, inject } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { catchError, firstValueFrom, of } from 'rxjs';
import {
  BillingOrganisation,
  BillingProfileApiService,
} from './billing-profile-api.service';
import { RoleService } from './role.service';
import {
  fillMissingProfileFields,
  hasInvoiceableAddress,
  profileFromBillDetail,
} from '../components/billing-details-form/billing-profile-validation';
import type { BillingProfile, UserBillDetail } from '../types/rest-api';

/**
 * Keeps the invoice address a buyer typed at checkout.
 *
 * Without this the address reached the order (and Stripe) and nothing else: the
 * backend stores it in `billing_accounts` only when the order names an
 * organisation, and no school-unit purchase does -- during onboarding the
 * school does not even exist yet. The organisation's profile therefore kept the
 * name and e-mail it was created with and no address, so the next purchase for
 * the same school hit `hasInvoiceableAddress` and asked for the address again,
 * contradicting the form's own «einmalig erfasst».
 *
 * Writing it back here rather than naming the organisation on the order is
 * deliberate: `school_id` on an order also moves the charge to that
 * organisation's Stripe customer and arms the «already has an active
 * subscription» guard. Payment routing stays exactly as it was.
 */
@Injectable({ providedIn: 'root' })
export class BillingProfileWriteBackService {
  private readonly billingApi = inject(BillingProfileApiService);
  /**
   * `RoleService` injects `SchoolApiService`, which injects this service --
   * resolving it eagerly would close a DI cycle, so it is looked up on use.
   */
  private readonly injector = inject(Injector);

  /**
   * Store what an order was billed to, if the organisation has no address yet.
   *
   * Called after the order succeeded, never before: an address typed for a
   * purchase that then failed belongs to no organisation.
   */
  afterOrder(
    billDetail: UserBillDetail | undefined,
    explicitOrg?: BillingOrganisation | null
  ): void {
    if (!billDetail) return;
    const org = this.profileOrganisation(
      explicitOrg ?? this.injector.get(RoleService).billingOrganisation
    );
    if (!org) {
      // Onboarding: the school this pays for does not exist yet, so there is no
      // profile to write to. Keep it until `createSchool` produces one.
      rememberPendingBillDetail(billDetail);
      return;
    }
    void this.writeBack(org, billDetail);
  }

  /** Write the address kept by {@link afterOrder} once the school exists. */
  async flushPendingFor(org: BillingOrganisation): Promise<void> {
    const pending = loadPendingBillDetail();
    clearPendingBillDetail();
    const target = this.profileOrganisation(org);
    if (pending && target) await this.writeBack(target, pending);
  }

  /**
   * Whose invoice details to write -- not the same as who the order was for.
   *
   * An order may name any school, because the backend walks up to whoever pays.
   * `PUT /v1/schools/{id}/billing-profile` does not: it refuses a unit inside a
   * Schulgemeinde and a sub-school. Without this the commonest buyer -- someone
   * working in a unit -- would keep being asked no matter what they type. Same
   * resolution the purchase dialogs already do
   * (single-job-posting-purchase-dialog.component.ts).
   */
  private profileOrganisation(
    org: BillingOrganisation | null
  ): BillingOrganisation | null {
    if (!org || org.kind !== 'school') return org;
    return (
      this.injector.get(RoleService).billingOrganisationForSchool(org.id) ?? org
    );
  }

  /**
   * Fill the organisation's blank address fields from a checkout.
   *
   * Best effort throughout: a purchase must never fail because we could not
   * store an address, and the worst case is the behaviour we have today --
   * being asked again.
   */
  async writeBack(
    org: BillingOrganisation,
    billDetail: UserBillDetail
  ): Promise<void> {
    const candidate = profileFromBillDetail(billDetail);
    // Placeholder payloads ('-', «Organization», noreply@) are what the older
    // purchase paths synthesised; storing those would recreate the very rows
    // `hasInvoiceableAddress` exists to reject.
    if (!hasInvoiceableAddress(candidate)) return;

    const stored = await this.loadProfile(org);
    // No row, or one we may not read: leave it alone rather than replace a
    // profile we cannot see. A 404 is handled the same way -- `PUT` refuses to
    // create a profile for an organisation that has none.
    if (!stored) return;
    // Already invoiceable: whatever is there was typed in the settings screen
    // or read back from Stripe, and both beat a checkout form.
    if (hasInvoiceableAddress(stored)) return;

    const merged = fillMissingProfileFields(stored, candidate);
    if (!merged) return;

    await firstValueFrom(
      this.billingApi.updateProfile(org, merged).pipe(
        catchError((err: unknown) => {
          console.warn('Rechnungsadresse konnte nicht gespeichert werden', err);
          return of(null);
        })
      )
    );
  }

  /**
   * A 400 means the school is billed through its Schulgemeinde and a 403 that
   * this user may not read the profile; neither is an invitation to write one.
   */
  private async loadProfile(
    org: BillingOrganisation
  ): Promise<BillingProfile | null> {
    return await firstValueFrom(
      this.billingApi.getProfile(org).pipe(
        catchError((err: unknown) => {
          if (!(err instanceof HttpErrorResponse) || err.status !== 404) {
            console.warn('Rechnungsprofil konnte nicht gelesen werden', err);
          }
          return of(null);
        })
      )
    );
  }
}

/**
 * The address of an order placed before its school existed.
 *
 * Session-scoped on purpose: it is only useful between paying and creating the
 * school, which happens in the same tab moments later.
 */
interface PendingBillDetail {
  billDetail: UserBillDetail;
  savedAt: string;
}

export const PENDING_BILL_DETAIL_KEY = 'bn_pending_bill_detail_v1';

/** Longer than the checkout round-trip, short enough not to outlive the intent. */
const PENDING_TTL_MS = 60 * 60 * 1000;

function rememberPendingBillDetail(billDetail: UserBillDetail): void {
  try {
    const payload: PendingBillDetail = {
      billDetail,
      savedAt: new Date().toISOString(),
    };
    sessionStorage.setItem(PENDING_BILL_DETAIL_KEY, JSON.stringify(payload));
  } catch {
    // Private mode or a full quota: the address is simply not kept.
  }
}

function loadPendingBillDetail(): UserBillDetail | null {
  try {
    const raw = sessionStorage.getItem(PENDING_BILL_DETAIL_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as PendingBillDetail;
    const age = Date.now() - new Date(parsed.savedAt).getTime();
    if (!Number.isFinite(age) || age > PENDING_TTL_MS) return null;
    return parsed.billDetail ?? null;
  } catch {
    return null;
  }
}

function clearPendingBillDetail(): void {
  try {
    sessionStorage.removeItem(PENDING_BILL_DETAIL_KEY);
  } catch {
    // Nothing to clean up if storage is unavailable.
  }
}
